import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import { AddIcon, GridIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Callout, Popover } from '@treams/treams-ui';

import SelectFeedbackQuestionTemplatesModal from '../../../../../feedback/settings/modals/SelectFeedbackQuestionTemplatesModal';
import FeedbackComposerCompetencies from './FeedbackComposerCompetencies';
import type { FeedbackCompetencySelection } from './FeedbackComposerCompetencies';
import FeedbackComposerGoals from './FeedbackComposerGoals';
import FeedbackComposerQuestions from './FeedbackComposerQuestions';

function FeedbackRequestModalQuestionsHint(props: {
	questions: (graphql.FeedbackRequestQuestionInput & { id: string })[];
}) {
	const { t } = useTranslation();
	if (props.questions.length < 3) {
		return null;
	}

	const text =
		props.questions.length >= 3 && props.questions.length < 5
			? t('feedback.composer.questions.hint.many')
			: t('feedback.composer.questions.hint.tooMany');

	return (
		<Box mb={2}>
			<Callout
				color={props.questions.length >= 5 ? 'warning' : 'blue'}
				title={t('feedback.composer.questions.hint.title')}
				description={text}
			/>
		</Box>
	);
}

export default function FeedbackComposer(props: {
	method: 'template' | 'custom' | 'competencies' | 'goals';
	userID: string;
	readOnly?: boolean;
	questions: (graphql.FeedbackRequestQuestionInput & { id: string })[];
	goals: { goalID: string; text?: string; order: number }[];
	competencies: FeedbackCompetencySelection[];
	onChange: (
		questions: (graphql.FeedbackRequestQuestionInput & { id: string })[],
		goals: { goalID: string; text?: string; order: number }[],
		competencies: FeedbackCompetencySelection[],
	) => void;
}) {
	const { t } = useTranslation();
	const { localize, language } = useLocalize();

	const [library, setLibrary] = useState(false);

	const [autoEditQuestionID, setAutoEditQuestionID] = useState<string | undefined>(undefined);

	return (
		<>
			{props.method !== 'goals' && props.method !== 'competencies' ? (
				props.readOnly ? (
					<FeedbackComposerQuestions readOnly questions={props.questions} onChange={() => {}} />
				) : (
				<>
					<FeedbackComposerQuestions
						questions={props.questions}
						autoEditQuestionID={autoEditQuestionID}
						onChange={(questions) => props.onChange(questions, props.goals, props.competencies)}
					/>
					<FeedbackRequestModalQuestionsHint questions={props.questions} />
					<Flex>
						<Box mr={2}>
							<Button
								title={t('feedback.composer.addQuery')}
								icon={<AddIcon />}
								onClick={() => {
									const id = Math.random().toString();
									const questions = props.questions.slice();
									questions.push({
										id,
										text: '',
										localizedText: [{ languageID: language.id, text: '' }],
										subTitle: '',
										type: graphql.FeedbackQuestionType.Text,
									});
									props.onChange(questions, props.goals, props.competencies);
									setAutoEditQuestionID(id);
								}}
								type="secondary"
							/>
						</Box>
						<Box>
							<Popover.Target
								visible={library}
								center
								contents={
									<SelectFeedbackQuestionTemplatesModal
										onCancel={() => setLibrary(false)}
										onSelect={(selection) => {
											const questions = props.questions.slice();

											if (questions.length === 1 && questions[0].text.trim().length === 0) {
												questions.splice(0, questions.length);
											}

											for (const question of selection) {
												const id = Math.random().toString();
												questions.push({
												id,
												text: localize(question.text).text,
												localizedText: question.text,
												subTitle: localize(question.subTitle ?? [])?.text ?? '',
												additionalDetails: localize(question.additionalDetails ?? [])?.text,
												type: question.type,
													options: (question.options ?? []).map((option) => ({
														id: option.id,
														text: option.text,
													})),
												});
											}
											props.onChange(questions, props.goals, props.competencies);
											setAutoEditQuestionID(undefined);
											setLibrary(false);
										}}
									/>
								}
							>
								<Button
									title={t('feedback.composer.selectQuery')}
									icon={<GridIcon />}
									onClick={() => setLibrary(true)}
									type="secondary"
								/>
							</Popover.Target>
						</Box>
					</Flex>
				</>
				)
			) : (
				<>
					{props.method === 'competencies' && (
						<Box mb={4}>
							<FeedbackComposerCompetencies
								userID={props.userID}
								competencies={props.competencies}
								onChange={(competencies) =>
									props.onChange(props.questions, props.goals, competencies)
								}
							/>
						</Box>
					)}
					{props.method === 'goals' && (
						<Box mb={4}>
							<FeedbackComposerGoals
								userID={props.userID}
								goals={props.goals}
								onChange={(goals) => props.onChange(props.questions, goals, props.competencies)}
							/>
						</Box>
					)}
				</>
			)}
		</>
	);
}
